<script setup lang="ts">
import { trackEvent } from '@@/bits/analytics'
import { __ } from '@@/bits/intl'
import { numberToHumanSize } from '@@/bits/numbers_helper'
import { FileSizeUnit } from '@@/bits/units'
import { LibraryType } from '@@/enums'
import OzBadge, { OzBadgeColorScheme } from '@@/library/v4/components/OzBadge.vue'
import OzBox, { OzBoxColors } from '@@/library/v4/components/OzBox.vue'
import OzContainedButton, {
  OzContainedButtonColorScheme,
  OzContainedButtonSizePreset,
} from '@@/library/v4/components/OzContainedButton.vue'
import OzDivider, { OzDividerColorScheme, OzDividerOrientation } from '@@/library/v4/components/OzDivider.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzLoadingSpinner from '@@/library/v4/components/OzLoadingSpinner.vue'
import OzMembershipTierGrid, { OzMembershipTierGridVariant } from '@@/library/v4/components/OzMembershipTierGrid.vue'
import { computed, ref } from 'vue'

defineEmits(['upgrade', 'continue', 'upgrade-monthly', 'upgrade-annual'])

const props = withDefaults(
  defineProps<{
    variant: Variant
    type?: Type // Determines whether the tier is a "Personal" tier or a "Library" tier.
    libraryType?: LibraryType | null // Type of Library. Ignored if type is "Personal".
    isExpanded: boolean
    isWhiteboard: boolean
    monthlyPrice?: string
    annualPrice?: string
    isLoadingPlanInformation: boolean
    isLoadingAdvertisedQuotas: boolean
    advertisedWhiteboardFrameQuota: number
    advertisedFileSizeQuota: string // The maximum upload file size allowed in this Tier.
    advertisedFileSizeQuotaUnit?: FileSizeUnit // The unit of the maximum upload file size allowed in this Tier.
    advertisedWallsQuota?: number // The maximum amount of walls allowed in this Tier.
    wallsUsed?: number // Number of walls used by the user/library. Used for quota variants.
    whiteboardFramesUsed?: number
    fileSize?: number // File size to display if the variant is "FileSize".
    isUserLoggedIn?: boolean // Whether the user is logged in or not. Used by the "SignUp" variant.
    continueUrl?: string // The URL to redirect in the "StartFreeTrial" variant.
    xChecked?: boolean // Determines whether to show the checkmark next to the tier name or not.
    annualSavingsPercentage?: number | null // The annual savings percentage for the Gold tier.
    colorScheme?: OzBoxColors // allow parent to override color scheme
  }>(),
  {
    type: Type.Personal,
    libraryType: null,
    monthlyPrice: undefined,
    annualPrice: undefined,
    advertisedFileSizeQuotaUnit: FileSizeUnit.MB,
    advertisedWallsQuota: undefined,
    wallsUsed: undefined,
    whiteboardFramesUsed: undefined,
    fileSize: undefined,
    isUserLoggedIn: undefined,
    continueUrl: undefined,
    xChecked: false,
    annualSavingsPercentage: null,
    colorScheme: undefined,
  },
)

const xPaymentSchedules = ref<boolean>(false)

const isPadletQuotaOrFileSizeVariant = computed<boolean>(
  () =>
    props.variant === Variant.UnderFrameQuota ||
    props.variant === Variant.AtFrameQuota ||
    props.variant === Variant.OverFrameQuota ||
    props.variant === Variant.UnderPadletQuota ||
    props.variant === Variant.AtPadletQuota ||
    props.variant === Variant.OverPadletQuota ||
    props.variant === Variant.FileSize,
)

const colorScheme = computed<OzBoxColors | null>(() => {
  if (props.colorScheme) return props.colorScheme
  if (isPadletQuotaOrFileSizeVariant.value) return OzBoxColors.Secondary
  if (
    props.variant === Variant.Upgrade ||
    props.variant === Variant.UpgradeSingleStep ||
    props.variant === Variant.Continue ||
    props.variant === Variant.StartFreeTrial
  )
    return OzBoxColors.Highlight
  return null
})

const showPaymentSchedules = (): void => {
  trackEvent('Billing', 'Chose Gold plan', null, null, null)
  xPaymentSchedules.value = true
}
</script>

<script lang="ts">
export enum Type {
  Personal,
  Library,
}

export enum Variant {
  UnderFrameQuota,
  AtFrameQuota,
  OverFrameQuota,
  Upgrade,
  UpgradeSingleStep,
  UnderPadletQuota,
  AtPadletQuota,
  OverPadletQuota,
  FileSize,
  SignUp,
  Continue,
  StartFreeTrial,
  VideoLengthLimit,
}

export default {}
</script>

<template>
  <OzBox :color="colorScheme">
    <OzMembershipTierGrid
      :class="[
        colorScheme === OzBoxColors.Secondary
          ? 'text-dark-text-300 dark:text-light-text-300'
          : 'text-dark-text-100 dark:text-light-text-100',
      ]"
      :is-expanded="isExpanded"
      :variant="
        variant === Variant.UpgradeSingleStep
          ? OzMembershipTierGridVariant.SingleStep
          : OzMembershipTierGridVariant.Default
      "
    >
      <template #first>
        <div class="flex items-center" :class="[!isExpanded && 'ps-1']">
          <p class="text-heading-3 font-semibold">
            <template v-if="type === Type.Personal">{{ __('Gold') }}</template>
            <template v-if="type === Type.Library">
              <template v-if="libraryType == LibraryType.Classroom">{{ __('Classroom') }}</template>
              <template v-else>{{ __('Team Gold') }}</template>
            </template>
          </p>
          <OzIcon
            v-if="xChecked"
            :size="16"
            class="text-grape-500 dark:text-canary-500 ms-2"
            name="checkmark_thicker"
          />
        </div>
      </template>
      <template #second>
        <div v-if="isLoadingAdvertisedQuotas" :class="[isExpanded && 'ms-4', !isExpanded && 'ms-2']">
          <OzLoadingSpinner :size="24" />
        </div>
        <div v-else class="text-body-posts" :class="[!isExpanded && 'ps-1']">
          <p v-if="type === Type.Personal">{{ __('%{wallQuota} padlets', { wallQuota: advertisedWallsQuota }) }}</p>
          <p v-if="type === Type.Library">{{ __('Unlimited padlets') }}</p>

          <slot v-else name="secondaryBenefitText">
            <p v-if="isWhiteboard">
              {{ __('%{frameLimit} card limit', { frameLimit: advertisedWhiteboardFrameQuota }) }}
            </p>
            <p v-else>
              {{
                advertisedFileSizeQuotaUnit === FileSizeUnit.MB
                  ? __('%{fileSizeLimit}MB /upload', {
                      fileSizeLimit: advertisedFileSizeQuota,
                    })
                  : __('%{fileSizeLimit}GB /upload', {
                      fileSizeLimit: advertisedFileSizeQuota,
                    })
              }}
            </p>
          </slot>
        </div>
      </template>
      <template #third>
        <div
          :class="[
            isExpanded && 'pe-0 flex items-center',
            !isExpanded && 'pe-1',
            type === Type.Personal && 'min-h-13',
            type === Type.Library && 'min-h-17.25',
          ]"
        >
          <div v-if="isLoadingPlanInformation" :class="[isExpanded && 'ms-4', !isExpanded && 'me-2']">
            <OzLoadingSpinner :size="24" />
          </div>
          <div v-else :class="['text-body-posts', isExpanded && 'text-start', !isExpanded && 'text-end']">
            <p v-if="monthlyPrice">{{ __('%{amount} /month', { amount: monthlyPrice }) }}</p>
            <p v-if="annualPrice">{{ __('%{amount} /year', { amount: annualPrice }) }}</p>
            <p
              v-if="type === Type.Library && libraryType == LibraryType.Team"
              class="text-body-extra-small text-dark-text-200 dark:text-light-text-200"
            >
              {{ __('per Maker') }}
            </p>
            <template v-if="monthlyPrice && annualSavingsPercentage">
              <OzBadge
                v-if="isExpanded"
                :color-scheme="
                  isPadletQuotaOrFileSizeVariant
                    ? OzBadgeColorScheme.DisabledInverse
                    : OzBadgeColorScheme.SelectionInverse
                "
                :text="__('Save %{discountPercentage}%', { discountPercentage: annualSavingsPercentage })"
                class="w-fit"
              />
              <p
                v-else
                :class="[
                  'text-body-small',
                  isPadletQuotaOrFileSizeVariant
                    ? 'text-dark-text-300 dark:text-light-text-300'
                    : 'text-grape-500 dark:text-canary-500',
                ]"
              >
                {{ __('Save %{discountPercentage}%', { discountPercentage: annualSavingsPercentage }) }}
              </p>
            </template>
          </div>
        </div>
      </template>
      <template #fourth>
        <slot name="endContent">
          <div v-if="type === Type.Personal && variant === Variant.UpgradeSingleStep" class="flex max-w-41">
            <OzContainedButton
              v-if="!xPaymentSchedules"
              :color-scheme="OzContainedButtonColorScheme.Orange"
              :size-preset="isExpanded ? OzContainedButtonSizePreset.H40px : OzContainedButtonSizePreset.H32px"
              :text="__('Upgrade')"
              data-testid="upgradeGoldMembershipButton"
              @click="showPaymentSchedules"
            />
            <template v-else>
              <OzContainedButton
                class="!rounded-e-none min-w-0"
                :color-scheme="OzContainedButtonColorScheme.Orange"
                :size-preset="OzContainedButtonSizePreset.H32px"
                :text="__('Monthly')"
                @click="$emit('upgrade-monthly')"
              />
              <OzDivider
                class="shrink-0"
                :color-scheme="OzDividerColorScheme.Translucent"
                :orientation="OzDividerOrientation.Vertical"
              />
              <OzContainedButton
                class="!rounded-s-none min-w-0"
                :color-scheme="OzContainedButtonColorScheme.Orange"
                :size-preset="OzContainedButtonSizePreset.H32px"
                :text="__('Annual')"
                @click="$emit('upgrade-annual')"
              />
            </template>
          </div>
          <OzContainedButton
            v-if="variant === Variant.Upgrade"
            :color-scheme="OzContainedButtonColorScheme.Orange"
            :size-preset="isExpanded ? OzContainedButtonSizePreset.H40px : OzContainedButtonSizePreset.H32px"
            :text="__('Upgrade')"
            data-testid="upgradeGoldMembershipButton"
            @click="$emit('upgrade')"
          />
          <div
            v-if="variant === Variant.UnderFrameQuota"
            :class="['text-end text-body-small', isExpanded && 'pe-2', !isExpanded && 'pe-1']"
          >
            <p>{{ __('%{whiteboardFramesUsed} cards made', { whiteboardFramesUsed }) }}</p>
            <p class="font-semibold">{{ __('under limit') }}</p>
          </div>
          <div
            v-if="variant === Variant.AtFrameQuota"
            :class="['text-end text-body-small', isExpanded && 'pe-2', !isExpanded && 'pe-1']"
          >
            <p>{{ __('%{whiteboardFramesUsed} cards made', { whiteboardFramesUsed }) }}</p>
            <p class="font-semibold">{{ __('at limit') }}</p>
          </div>
          <div
            v-if="variant === Variant.OverFrameQuota"
            :class="['text-end text-body-small', isExpanded && 'pe-2', !isExpanded && 'pe-1']"
          >
            <p>{{ __('%{whiteboardFramesUsed} cards made', { whiteboardFramesUsed }) }}</p>
            <p class="font-semibold">{{ __('over limit') }}</p>
          </div>
          <div
            v-if="variant === Variant.UnderPadletQuota"
            :class="['text-end text-body-small', isExpanded && 'pe-2', !isExpanded && 'pe-1']"
          >
            <p>{{ __('%{wallsUsed} padlets made', { wallsUsed }) }}</p>
            <p class="font-semibold">{{ __('under limit') }}</p>
          </div>
          <div
            v-if="variant === Variant.AtPadletQuota"
            :class="['text-end text-body-small', isExpanded && 'pe-2', !isExpanded && 'pe-1']"
          >
            <p>{{ __('%{wallsUsed} padlets made', { wallsUsed }) }}</p>
            <p class="font-semibold">{{ __('at limit') }}</p>
          </div>
          <div
            v-if="variant === Variant.OverPadletQuota"
            :class="['text-end text-body-small', isExpanded && 'pe-2', !isExpanded && 'pe-1']"
          >
            <p>{{ __('%{wallsUsed} padlets made', { wallsUsed }) }}</p>
            <p class="font-semibold">{{ __('over limit') }}</p>
          </div>
          <div
            v-if="variant === Variant.FileSize"
            :class="['text-end text-body-small', isExpanded && 'pe-2', !isExpanded && 'pe-1']"
          >
            <p>{{ __('%{fileSize} file', { fileSize: numberToHumanSize(fileSize) }) }}</p>
            <p class="font-semibold">{{ __('exceeds limit') }}</p>
          </div>

          <OzContainedButton
            v-if="variant === Variant.SignUp && type === Type.Personal"
            :size-preset="isExpanded ? OzContainedButtonSizePreset.H40px : OzContainedButtonSizePreset.H32px"
            :text="isUserLoggedIn ? __('Go to billing') : __('Sign up')"
            :href="isUserLoggedIn ? '/dashboard/settings/billing' : '/auth/signup'"
          />
          <OzContainedButton
            v-if="variant === Variant.SignUp && type === Type.Library"
            :size-preset="isExpanded ? OzContainedButtonSizePreset.H40px : OzContainedButtonSizePreset.H32px"
            :text="isUserLoggedIn ? __('Create team') : __('Sign up')"
            :color-scheme="OzContainedButtonColorScheme.Blue"
            :href="isUserLoggedIn ? '/account/create/team' : '/auth/signup'"
          />
          <OzContainedButton
            v-if="variant === Variant.Continue"
            :color-scheme="OzContainedButtonColorScheme.Orange"
            :size-preset="isExpanded ? OzContainedButtonSizePreset.H40px : OzContainedButtonSizePreset.H32px"
            :text="__('Continue')"
            @click="$emit('continue')"
          />
          <OzContainedButton
            v-if="variant === Variant.StartFreeTrial"
            :color-scheme="OzContainedButtonColorScheme.Orange"
            :size-preset="isExpanded ? OzContainedButtonSizePreset.H40px : OzContainedButtonSizePreset.H32px"
            :text="__('Start free trial')"
            :href="continueUrl"
          />
        </slot>
      </template>
    </OzMembershipTierGrid>
  </OzBox>
</template>
